import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Email from "./Email";
import Phone from "./Phone";
import Address from "./Address";
import { deepFreeze, timestampToDate } from "../util/misc";
import { every, get, isArray, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const CONTACT_FIELDS = {
  name: "",
  type: "",
  company: "",
  companyID: "",
  position: [],
  emails: [],
  phones: [],
  addresses: [],
  notes: "",

  // Meta info

  created: null,
  createdBy: null,
  disabled: null,
  disabledReason: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(CONTACT_FIELDS);

export const SEARCHABLE_FIELDS = [
  "data.name",
  "data.type",
  "data.company",
  "data.position",
  "data.emails.address",
  "data.phones.number",
  "data.addresses.street",
  "data.addresses.city",
  "data.addresses.zip",
  "data.notes",
];
deepFreeze(SEARCHABLE_FIELDS);

export const CONTACT_TYPES = {
  designer: {
    label: "Architect/Designer",
    icon: "designer",
  },
  contractor: {
    label: "Builder/Contractor",
    icon: "contractor",
  },
  employee: {
    label: "Employee",
    icon: "user",
  },
  owner: {
    label: "Homeowner",
    icon: "homeowner",
  },
  vendor: {
    label: "Vendor",
    icon: "vendor",
  },
  other: {
    label: "Other",
    icon: "contact",
  },
};
deepFreeze(CONTACT_TYPES);

export default class Contact extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, CONTACT_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.disabled = timestampToDate(data.disabled);
    data.modified = timestampToDate(data.modified);

    // Backward compatibility
    if (!isArray(data.position)) {
      data.position = data.position ? data.position.split(", ") : [];
    }

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      create: Contact.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    if (this.isEmpty) {
      return true;
    }
    if (this.isUser || this.isCompany) {
      return false;
    }
    return user.isEmployee;
  }

  canBeDeletedBy() {
    return false;
  }

  // Database

  get path() {
    return Contact.getPath(this.id);
  }

  static getPath(id) {
    let path = `contacts`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(contact) {
    return contact.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Contact(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      name: [
        (a) => Boolean(a && a.trim().length) || "required",
        (a) => !/^[A-Z]+\s+[A-Z]+$/.test(a) || "notAllCaps",
      ],
      company: [
        (a) => !a.trim() || !/^[A-Z]+\s+[A-Z]+$/.test(a) || "notAllCaps",
      ],
      type: [(a) => (a && a in CONTACT_TYPES) || "required"],
      emails: [
        () =>
          (this.data.emails.length > 0 &&
            this.emails.every((email) => email.isValid)) ||
          "required",
      ],
      phones: [
        () => this.phones.every((phone) => phone.isValid) || "phonesInvalid",
      ],
      addresses: [
        () =>
          this.addresses.every((address) => address.isValid) ||
          "addressesInvalid",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return !this.id && isEmpty(diff(this.data, CONTACT_FIELDS));
  }

  get isComplete() {
    let emails = this.data.emails.concat();
    let phones = this.data.phones.concat();
    let addresses = this.data.addresses.concat();

    if (this.data.companyID) {
      const company = this.company;
      if (company) {
        emails = emails.concat(company.data.emails);
        phones = phones.concat(company.data.phones);
        addresses = addresses.concat(company.data.addresses);
      }
    }

    return emails.length && phones.length && addresses.length;
  }

  isEqual(contact) {
    return this.id === contact.id && isEmpty(diff(this.data, contact.data));
  }

  // Formatting

  get user() {
    return this.id ? store.state.main.users[this.id] || null : null;
  }

  get isUser() {
    return Boolean(store.state.main.users && this.id in store.state.main.users);
  }

  get isCompany() {
    return Boolean(
      store.state.main.companies && this.id in store.state.main.companies
    );
  }

  // The Company account this contact represents when the contact IS the
  // company (company contacts share the company doc's id). Distinct from
  // `company`, which is the company a person contact belongs to.
  get companyAccount() {
    return this.isCompany ? store.state.main.companies[this.id] : null;
  }

  get company() {
    return this.data.companyID
      ? store.state.main.companies[this.data.companyID] || null
      : this.data.company;
  }

  get type() {
    return CONTACT_TYPES[this.data.type] || CONTACT_TYPES.other;
  }

  get icon() {
    return this.data.type === "employee" && this.isUser
      ? this.user.roles[0].icon
      : this.type.icon;
  }

  get position() {
    return isArray(this.data.position)
      ? this.data.position.join(", ")
      : this.data.position || "";
  }

  get emails() {
    return this.data.emails.map((email) => new Email(email));
  }

  get phones() {
    return this.data.phones.map((phone) => new Phone(phone));
  }

  get addresses() {
    return this.data.addresses.map((address) => new Address(address));
  }

  get balance() {
    const balance = store.state.main.balances[this.id];
    return balance ? balance.total : null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  toString() {
    return this.data.name;
  }
}
